import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
    makeStyles,
    Spinner,
} from '@fluentui/react-components';
import { useSelector } from 'react-redux';
import { RootState } from '../../utils/store';
import {
    CopilotService,
    FieldValueFieldEntry,
    FieldValueSearchEntry,
    FieldValueDetailEntry,
} from '../../Service/ServerAPI';
import { useTokenService } from '../../tokenService';

const useStyles = makeStyles({
    overlay: {
        position: 'absolute',
        bottom: '100%',
        left: 0,
        right: 0,
        maxHeight: '300px',
        overflowY: 'auto',
        backgroundColor: '#2D2D30',
        border: '1px solid #4D4F51',
        borderRadius: '8px',
        marginBottom: '4px',
        zIndex: 1000,
        boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
        '::-webkit-scrollbar': {
            width: '6px',
        },
        '::-webkit-scrollbar-thumb': {
            backgroundColor: '#555',
            borderRadius: '3px',
        },
    },
    item: {
        padding: '8px 12px',
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        ':hover': {
            backgroundColor: '#094771',
        },
    },
    activeItem: {
        backgroundColor: '#094771',
    },
    fieldName: {
        color: '#E0E0E0',
        fontWeight: 600,
    },
    schemaLabel: {
        color: '#888',
        fontSize: '11px',
        marginLeft: '6px',
    },
    valueItem: {
        paddingLeft: '24px',
        padding: '6px 12px 6px 24px',
        cursor: 'pointer',
        ':hover': {
            backgroundColor: '#094771',
        },
    },
    valueText: {
        color: '#CCC',
    },
    countBadge: {
        color: '#888',
        fontSize: '11px',
        marginLeft: '8px',
    },
    loading: {
        padding: '12px',
        display: 'flex',
        justifyContent: 'center',
    },
    noResults: {
        padding: '12px',
        color: '#888',
        textAlign: 'center' as const,
    },
    headerRow: {
        display: 'flex',
        alignItems: 'center',
    },
    boldMatch: {
        fontWeight: 700,
        color: '#fff',
    },
});

interface FieldValuesMenuProps {
    query: string; // text after the trigger character
    selectedField: string | null;
    onSelectField: (field: string, schema: string) => void;
    onSelectValue: (field: string, value: string, schema: string) => void;
    onClose: () => void;
    activeIndex: number;
    onItemCountChange: (count: number) => void;
    onItemsChange?: (items: MenuItem[]) => void;
}

type MenuItem =
    | { type: 'field'; field: string; schema: string; count?: number }
    | { type: 'value'; field: string; schema: string; value: string };

const FieldValuesMenu: React.FC<FieldValuesMenuProps> = ({
    query,
    selectedField,
    onSelectField,
    onSelectValue,
    onClose,
    activeIndex,
    onItemCountChange,
    onItemsChange,
}) => {
    const classes = useStyles();
    const settings = useSelector((state: RootState) => state.settings);
    const tokenService = useTokenService();
    const copilotService = CopilotService.getInstance(tokenService);
    const [loading, setLoading] = useState(false);
    const [items, setItems] = useState<MenuItem[]>([]);
    const menuRef = useRef<HTMLDivElement>(null);
    const debounceRef = useRef<ReturnType<typeof setTimeout>>();
    const activeItemRef = useRef<HTMLDivElement>(null);

    // Scroll active item into view
    useEffect(() => {
        activeItemRef.current?.scrollIntoView({ block: 'nearest' });
    }, [activeIndex]);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const params: { q?: string; field?: string } = {};
            if (selectedField) {
                params.field = selectedField;
                if (query.length >= 2) params.q = query;
            } else if (query.length >= 2) {
                params.q = query;
            }
            // If query is 0-1 chars and no field selected: Mode 1 (list all fields)

            const dataset = settings.sessionDataset || settings.dataset;
            const result = await copilotService.searchFieldValues(
                Object.keys(params).length > 0 ? params : undefined,
                settings.apiEndpoint,
                dataset
            );

            const menuItems: MenuItem[] = [];

            if (selectedField) {
                // Mode 3 or 4: single field detail
                const detail = result as FieldValueDetailEntry;
                if (detail?.values) {
                    for (const v of detail.values) {
                        menuItems.push({
                            type: 'value',
                            field: detail.field,
                            schema: detail.schema,
                            value: v,
                        });
                    }
                }
            } else if (Array.isArray(result)) {
                if (result.length === 0) {
                    // empty
                } else if ('count' in result[0]) {
                    // Mode 1: field list
                    for (const entry of result as FieldValueFieldEntry[]) {
                        menuItems.push({
                            type: 'field',
                            field: entry.field,
                            schema: entry.schema,
                            count: entry.count,
                        });
                    }
                } else if ('fieldMatch' in result[0]) {
                    // Mode 2: search results
                    for (const entry of result as FieldValueSearchEntry[]) {
                        menuItems.push({
                            type: 'field',
                            field: entry.field,
                            schema: entry.schema,
                        });
                        if (entry.values) {
                            for (const v of entry.values) {
                                menuItems.push({
                                    type: 'value',
                                    field: entry.field,
                                    schema: entry.schema,
                                    value: v,
                                });
                            }
                        }
                    }
                }
            }

            setItems(menuItems);
            onItemCountChange(menuItems.length);
            onItemsChange?.(menuItems);
        } catch {
            setItems([]);
            onItemCountChange(0);
        } finally {
            setLoading(false);
        }
    }, [query, selectedField, settings.apiEndpoint, copilotService, onItemCountChange, onItemsChange]);

    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(fetchData, 200);
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, [fetchData]);

    const highlightMatch = (text: string, match: string) => {
        if (!match || match.length < 2) return <>{text}</>;
        const idx = text.toLowerCase().indexOf(match.toLowerCase());
        if (idx === -1) return <>{text}</>;
        return (
            <>
                {text.slice(0, idx)}
                <span className={classes.boldMatch}>
                    {text.slice(idx, idx + match.length)}
                </span>
                {text.slice(idx + match.length)}
            </>
        );
    };

    const handleItemClick = (item: MenuItem) => {
        if (item.type === 'field') {
            onSelectField(item.field, item.schema);
        } else {
            onSelectValue(item.field, item.value, item.schema);
        }
    };

    return (
        <div className={classes.overlay} ref={menuRef}>
            {loading && items.length === 0 && (
                <div className={classes.loading}>
                    <Spinner size="tiny" />
                </div>
            )}

            {!loading && items.length === 0 && (
                <div className={classes.noResults}>
                    No results found
                </div>
            )}

            {items.map((item, idx) => {
                const isActive = idx === activeIndex;
                if (item.type === 'field') {
                    return (
                        <div
                            key={`field-${item.schema}-${item.field}`}
                            ref={isActive ? activeItemRef : undefined}
                            className={`${classes.item} ${isActive ? classes.activeItem : ''}`}
                            onClick={() => handleItemClick(item)}
                        >
                            <div className={classes.headerRow}>
                                <span className={classes.fieldName}>
                                    {highlightMatch(item.field, query)}
                                </span>
                                <span className={classes.schemaLabel}>
                                    {item.schema}
                                </span>
                                {item.count !== undefined && (
                                    <span className={classes.countBadge}>
                                        ({item.count} values)
                                    </span>
                                )}
                            </div>
                        </div>
                    );
                } else {
                    return (
                        <div
                            key={`value-${item.schema}-${item.field}-${item.value}`}
                            ref={isActive ? activeItemRef : undefined}
                            className={`${classes.valueItem} ${isActive ? classes.activeItem : ''}`}
                            onClick={() => handleItemClick(item)}
                        >
                            <span className={classes.valueText}>
                                {highlightMatch(item.value, query)}
                            </span>
                        </div>
                    );
                }
            })}
        </div>
    );
};

export type { MenuItem };
export default FieldValuesMenu;
